import { Box, Typography } from '@mui/material';

import {
  DEFAULT_DOCUMENT_FONT,
  isSupportedDocumentFont,
  toSafeDocumentColor,
} from '../../config/branding';

export interface DocumentStylePreviewProps {
  /** `#RRGGBB` — main headings. */
  heading: string;
  /** `#RRGGBB` — sub-headings, eyebrows, numbered-section labels. */
  subheading: string;
  /** `#RRGGBB` — body copy. Falls back to `heading` when absent, as the backend does. */
  body?: string;
  /** `#RRGGBB` — callouts and emphasis. */
  highlight: string;
  /** The org's BODY face, or empty for the house default. */
  font?: string;
  /** The org's HEADING face. Falls back to the body face, as the backend does. */
  headingFont?: string;
  /** The org's document header logo, if one is uploaded. */
  logoUrl?: string;
}

/**
 * A miniature of a generated document, drawn from the org's own style settings.
 *
 * The Branding card used to ask an admin to pick colours and a typeface for a
 * document they could not see, and then explained the consequences in three
 * paragraphs of help text. Showing the result replaces most of that prose: a
 * grey-on-white heading or an unreadable sub-heading is obvious here and
 * invisible in a hex code.
 *
 * REPRESENTATIVE, NOT EXACT. Two things it cannot honour:
 *   - the org's brand font only renders if it happens to be installed on the
 *     admin's machine, so the preview falls back where the document would not;
 *   - the real masthead carries the org's logo at print resolution.
 * It is a proof of the COLOUR RELATIONSHIPS — which is the part that goes
 * wrong — not a proof of the finished artifact. The caption says so.
 */
export function DocumentStylePreview({
  heading,
  subheading,
  body,
  highlight,
  font,
  headingFont,
  logoUrl,
}: DocumentStylePreviewProps) {
  // Two things have to be true of the face before it reaches `sx`.
  //
  // QUOTED: a family name with a space is only valid CSS unquoted-free, and
  // several supported faces have one ("Times New Roman", "Leitura Sans Grot 2").
  // Unquoted, the whole declaration is invalid and dropped — so the preview
  // silently showed the default face for exactly the brand fonts it exists to
  // demonstrate.
  //
  // ALLOW-LISTED: `sx` compiles to a real stylesheet via emotion, so a stored
  // value containing a double-quote could close this declaration and append
  // arbitrary CSS rules. Quoting alone does not stop that. The font is already
  // constrained to SUPPORTED_DOCUMENT_FONTS at the picker and again in the
  // backend's validator, so anything else is either stale or hostile and the
  // default is the right answer for both.
  const toFace = (requested: string | undefined, fallback: string) => {
    // Falls through the SAME chain the backend does rather than jumping straight
    // to the platform default: an unsupported heading face resolves to the body
    // face there, so short-circuiting to Calibri here would preview a document
    // nobody receives. (ai_review !207, defects.)
    const wanted = isSupportedDocumentFont(requested)
      ? (requested as string)
      : isSupportedDocumentFont(fallback)
        ? fallback
        : DEFAULT_DOCUMENT_FONT;
    return `"${wanted}"`;
  };
  // Two faces (Trello #645). The heading face falls back to the BODY face, not
  // to the platform default — that is the backend's own chain, and showing
  // Calibri headings over an Arial body would preview a document nobody gets.
  const face = toFace(font, DEFAULT_DOCUMENT_FONT);
  const headingFace = toFace(headingFont, font || DEFAULT_DOCUMENT_FONT);

  // The colours reach `sx` by the same route as the font, so they need the same
  // guard. Shared with the swatch tiles on the org page — see
  // toSafeDocumentColor.
  const ink = toSafeDocumentColor(heading);
  const sub = toSafeDocumentColor(subheading);
  // Body copy follows the heading colour until an org sets its own — the same
  // fallback resolveColorScheme applies, so the preview cannot claim a split
  // the document will not have.
  const bodyInk = toSafeDocumentColor(body || heading);
  const hi = toSafeDocumentColor(highlight);

  return (
    <Box
      data-testid="document-style-preview"
      sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
          Live preview
        </Typography>
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
          Quote Comparison
        </Typography>
      </Box>

      {/* The page. Fixed A4-ish ratio so it reads as a document rather than a
          panel, and pure white regardless of admin theme — the client's page
          is white, and previewing it on a dark ground would misrepresent every
          colour choice on it. */}
      <Box data-testid="preview-page" sx={{ bgcolor: '#ffffff', fontFamily: face }}>
        <Box
          sx={{
            height: 40,
            bgcolor: '#0A1E32',
            display: 'flex',
            alignItems: 'center',
            justifyContent: logoUrl ? 'space-between' : 'flex-end',
            px: 1.25,
          }}
        >
          {logoUrl && (
            <Box
              component="img"
              src={logoUrl}
              alt=""
              sx={{ maxHeight: 22, maxWidth: '45%', objectFit: 'contain' }}
            />
          )}
          <Typography
            sx={{ color: '#fff', fontSize: 7, letterSpacing: '0.12em', textTransform: 'uppercase' }}
          >
            Privileged &amp; confidential
          </Typography>
        </Box>

        <Box sx={{ px: 1.75, py: 1.5 }}>
          <Typography
            data-testid="preview-eyebrow"
            sx={{
              color: sub,
              fontSize: 7,
              letterSpacing: '0.13em',
              textTransform: 'uppercase',
              mb: 0.5,
            }}
          >
            Policy quote comparison
          </Typography>
          <Typography
            data-testid="preview-title"
            sx={{
              color: ink,
              fontFamily: headingFace,
              fontSize: 15,
              fontWeight: 700,
              lineHeight: 1.15,
              mb: 0.75,
            }}
          >
            Classic Oriental Trading 533
          </Typography>
          <Typography
            data-testid="preview-standfirst"
            sx={{ color: bodyInk, fontSize: 8, lineHeight: 1.6 }}
          >
            A like-for-like review of the incumbent combined policy against two quotations that
            together would replace it.
          </Typography>

          <Box data-testid="preview-rule" sx={{ height: 2, bgcolor: ink, mt: 1.5, mb: 0.75 }} />
          <Typography
            data-testid="preview-subheading"
            sx={{ fontFamily: headingFace, fontSize: 9, fontWeight: 700, mb: 0.5 }}
          >
            <Box component="span" sx={{ color: sub }}>01</Box>
            {/* The section TITLE takes the sub-heading colour, not the heading
                colour it used to take. That matches the renderer: colorForRun in
                titan-style gives `size === sectionTitleSize && bold` the
                sub-heading role, so the old `ink` here overstated nothing — it
                UNDERSTATED the split, showing a section title in the heading
                colour the document never paints it in. (ai_review !207.) */}
            <Box
              data-testid="preview-section-title"
              component="span"
              sx={{ color: sub }}
            >&nbsp;&nbsp;Client requirements</Box>
          </Typography>
          <Typography
            data-testid="preview-body"
            sx={{ color: bodyInk, fontSize: 8, lineHeight: 1.6 }}
          >
            The client operates a mixed commercial fleet alongside two trading premises.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mt: 1.25, fontSize: 7.5, fontWeight: 700 }}>
            <Box component="span" sx={{ color: sub }}>Adequate</Box>
            <Box component="span" sx={{ color: hi }}>Review</Box>
            {/* The Gap token is a fixed burden signal — no org can re-colour it,
                so it is drawn from the same constant the templates use. */}
            <Box component="span" sx={{ color: '#B0413E' }}>Gap</Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 1.5, py: 1, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', lineHeight: 1.5 }}>
          Representative, not exact — a brand font renders here only if it is installed on this
          machine.
        </Typography>
      </Box>
    </Box>
  );
}

export default DocumentStylePreview;
